/**
 * Token-bound accent card for repeated symbol/title/body panel surfaces.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type { MonographPanelTokens } from "./MonographPanelPrimitiveTypes.js";

export interface MonographAccentCardProps {
  readonly tokens: MonographPanelTokens;
  readonly title: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly symbol?: React.ReactNode;
  readonly eyebrow?: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly accent?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
  readonly contentStyle?: React.CSSProperties;
}

export function MonographAccentCard({
  tokens,
  title,
  children,
  symbol,
  eyebrow,
  footer,
  accent = tokens.accent,
  className,
  style,
  contentStyle,
}: MonographAccentCardProps): React.JSX.Element {
  return (
    <article
      className={cn("csk-monograph-accent-card", className)}
      style={{
        background: `linear-gradient(160deg, ${colorWithAlpha(accent, "12")}, rgba(4,6,15,0.58))`,
        border: `1px solid ${colorWithAlpha(accent, "55")}`,
        borderRadius: 12,
        boxShadow: `0 8px 28px ${colorWithAlpha(accent, "1c")}`,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: "14px 16px",
        ...style,
      }}
    >
      <header style={{ alignItems: "center", display: "flex", gap: 10 }}>
        {symbol ? (
          <span
            aria-hidden
            style={{
              alignItems: "center",
              background: colorWithAlpha(accent, "24"),
              borderRadius: 10,
              color: accent,
              display: "inline-flex",
              flexShrink: 0,
              fontFamily: tokens.mono,
              fontSize: 14,
              fontWeight: 800,
              height: 32,
              justifyContent: "center",
              lineHeight: 1,
              minWidth: 36,
              padding: "0 8px",
              textShadow: `0 0 14px ${colorWithAlpha(accent, "66")}`,
            }}
          >
            {symbol}
          </span>
        ) : null}
        <div style={{ minWidth: 0 }}>
          <div style={{ color: tokens.bright, fontSize: 14, fontWeight: 800 }}>{title}</div>
          {eyebrow ? (
            <div
              style={{
                color: tokens.muted,
                fontFamily: tokens.mono,
                fontSize: 11,
                letterSpacing: 0,
                marginTop: 2,
              }}
            >
              {eyebrow}
            </div>
          ) : null}
        </div>
      </header>
      {children ? (
        <div
          style={{
            color: tokens.text,
            fontSize: 13,
            lineHeight: 1.6,
            ...contentStyle,
          }}
        >
          {children}
        </div>
      ) : null}
      {footer ? <div style={{ marginTop: "auto" }}>{footer}</div> : null}
    </article>
  );
}
